<template>
  <div class="protocol-script">
    <a-card>
      <div class="header-bar">
        <span class="page-title">协议脚本</span>
        <a-button type="primary" @click="openDrawer">+ 创建脚本</a-button>
      </div>
      <div class="search-bar">
        <div class="search-panel">
          <a-form layout="inline">
            <a-form-item label="产品名称">
              <a-input placeholder="请输入脚本名称" style="width:180px;" />
            </a-form-item>
            <a-form-item label="脚本类型">
              <a-select placeholder="请选择脚本类型" style="width:180px;">
                <a-select-option value="">全部</a-select-option>
                <a-select-option value="up-pre">上报预处理</a-select-option>
                <a-select-option value="up-post">上报后处理</a-select-option>
                <a-select-option value="down-pre">下发预处理</a-select-option>
                <a-select-option value="down-post">下发后处理</a-select-option>
              </a-select>
            </a-form-item>
            <a-form-item label="脚本状态">
              <a-select placeholder="请选择脚本状态" style="width:180px;">
                <a-select-option value="">全部</a-select-option>
                <a-select-option value="enabled">启用</a-select-option>
                <a-select-option value="disabled">禁用</a-select-option>
              </a-select>
            </a-form-item>
            <a-form-item label="操作来源">
              <a-select placeholder="请选择脚本来源" style="width:180px;">
                <a-select-option value="">全部</a-select-option>
                <a-select-option value="expand">拓展</a-select-option>
                <a-select-option value="gateway-sub">网关子设备</a-select-option>
                <a-select-option value="log">设备日志</a-select-option>
                <a-select-option value="ota">固件升级</a-select-option>
                <a-select-option value="model">物模型</a-select-option>
              </a-select>
            </a-form-item>
            <a-form-item>
              <a-button type="primary" style="margin-right:8px;">查询</a-button>
              <a-button>重置</a-button>
            </a-form-item>
          </a-form>
        </div>
      </div>
      <div class="script-list">
        <a-row gutter="16">
          <a-col :span="8" v-for="(item, idx) in scriptList" :key="item.id">
            <a-card class="script-card">
              <div class="card-header">
                <span class="script-title">脚本名称：{{ item.name }}</span>
                <div class="card-actions">
                  <a-switch
                    :checked="item.enabled"
                    checked-children="启用"
                    un-checked-children="启用"
                    @change="() => toggleEnable(idx)"
                    style="margin-right:12px;"
                  />
                  <a-button type="text" danger><template #icon><DeleteOutlined /></template></a-button>
                </div>
              </div>
              <div class="card-body">
                <div>脚本类型: {{ item.type }}</div>
                <div>操作来源: {{ item.source }}</div>
                <div>操作类型: {{ item.opType }}</div>
              </div>
            </a-card>
          </a-col>
        </a-row>
      </div>
    </a-card>
    <a-drawer
      :open="drawerOpen"
      title="创建脚本"
      placement="right"
      width="700px"
      @close="closeDrawer"
      :closable="true"
    >
      <a-form layout="vertical" style="padding-right:24px;">
        <a-form-item label="脚本名称" required>
          <a-input v-model:value="scriptForm.name" placeholder="请输入脚本名称" />
        </a-form-item>
        <div style="margin-bottom:16px;font-weight:bold;">脚本类型</div>
        <a-row gutter="16" style="margin-bottom:24px;">
          <a-col :span="12" v-for="item in scriptTypeOptions" :key="item.value">
            <div
              class="script-type-card"
              :class="{ active: scriptForm.type === item.value }"
              @click="selectScriptType(item.value)"
            >
              <div class="type-title">{{ item.label }}</div>
              <div class="type-desc">{{ item.desc }}</div>
            </div>
          </a-col>
        </a-row>
        <a-form-item label="选择来源" required>
          <a-select v-model:value="scriptForm.source" placeholder="请选择来源">
            <a-select-option v-for="item in sourceOptions" :key="item.value" :value="item.value">{{ item.label }}</a-select-option>
          </a-select>
        </a-form-item>
        <a-form-item label="操作类型" required>
          <a-select v-model:value="scriptForm.opType" placeholder="请选择操作类型">
            <a-select-option v-for="item in opTypeOptions" :key="item.value" :value="item.value">{{ item.label }}</a-select-option>
          </a-select>
        </a-form-item>
        <a-form-item label="描述">
          <a-textarea v-model:value="scriptForm.desc" placeholder="请输入描述" rows="3" />
        </a-form-item>
        <a-form-item>
          <a-button type="primary" html-type="submit">提交</a-button>
        </a-form-item>
      </a-form>
    </a-drawer>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import { DeleteOutlined } from '@ant-design/icons-vue'
import { protocolDataList } from '@/data/protocolData' // 导入协议数据

// 脚本类型选项
const scriptTypeValues = ['上报预处理', '上报后处理', '下发预处理', '下发后处理']
// 操作来源选项
const sourceValues = ['拓展', '网关子设备', '设备日志', '固件升级', '物模型']
// 操作类型选项 - 扩展
const opTypeValues = [
  '读取属性',
  '设置属性',
  '调用服务',
  '事件上报',
  '设备生命周期管理',
  '数据校验',
  '数据转换',
  '告警处理',
  '远程配置',
  '日志记录'
]

const scriptList = ref(
  protocolDataList.map((protocol, index) => ({
    id: index + 1,
    name: `${protocol.name}脚本`, // 使用协议名称作为脚本名称的一部分
    type: scriptTypeValues[Math.floor(Math.random() * scriptTypeValues.length)],
    source: sourceValues[Math.floor(Math.random() * sourceValues.length)],
    opType: opTypeValues[Math.floor(Math.random() * opTypeValues.length)],
    enabled: Math.random() < 0.7 // 70%的概率为启用
  }))
)

function toggleEnable(idx: number) {
  scriptList.value[idx].enabled = !scriptList.value[idx].enabled
}

const drawerOpen = ref(false)
const scriptForm = ref({
  name: '',
  type: 'up-pre',
  source: '',
  opType: '',
  desc: ''
})
const scriptTypeOptions = [
  { value: 'up-pre', label: '上报预处理', desc: '在设备属性上报时执行预处理操作' },
  { value: 'up-post', label: '上报后处理', desc: '在设备属性上报完成后执行操作' },
  { value: 'down-pre', label: '下发预处理', desc: '在设备属性下发时执行预处理操作' },
  { value: 'down-post', label: '下发后处理', desc: '在设备属性下发完成后执行操作' }
]
const sourceOptions = [
  // { value: '', label: '全部' }, // 创建时一般不选全部
  { value: 'expand', label: '拓展' },
  { value: 'gateway-sub', label: '网关子设备' },
  { value: 'log', label: '设备日志' },
  { value: 'ota', label: '固件升级' },
  { value: 'model', label: '物模型' }
]
const opTypeOptions = opTypeValues.map(op => ({ value: op, label: op })) // 从 opTypeValues 生成选项

function openDrawer() {
  drawerOpen.value = true
}
function closeDrawer() {
  drawerOpen.value = false
}
function selectScriptType(val: string) {
  scriptForm.value.type = val
}
</script>

<style lang="less" scoped>
.protocol-script {
  .header-bar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 16px;
    .page-title {
      font-size: 20px;
      font-weight: bold;
    }
  }
  .search-bar {
    margin-bottom: 16px;
    .search-panel {
      background: #fafbfc;
      border-radius: 4px;
      padding: 16px;
    }
  }
  .script-list {
    margin-top: 16px;
    .script-card {
      background: linear-gradient(90deg, #f7fafd 0%, #f5f8fb 100%);
      border-radius: 8px;
      margin-bottom: 16px;
      .card-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        .script-title {
          font-weight: bold;
          font-size: 16px;
        }
        .card-actions {
          display: flex;
          align-items: center;
        }
      }
      .card-body {
        margin-top: 8px;
        color: #666;
        font-size: 14px;
        line-height: 2;
      }
    }
  }
}
.script-type-card {
  border: 1px solid #e5e6eb;
  border-radius: 8px;
  background: #fafbfc;
  padding: 18px 20px 12px 20px;
  cursor: pointer;
  margin-bottom: 8px;
  transition: border-color 0.2s, box-shadow 0.2s;
  .type-title {
    font-weight: bold;
    font-size: 16px;
    margin-bottom: 4px;
  }
  .type-desc {
    color: #888;
    font-size: 13px;
  }
  &.active {
    border-color: #1677ff;
    background: #f0f7ff;
    box-shadow: 0 0 0 2px #e6f4ff;
  }
}
</style>